import { AgencyFeeMonth, AgencyRow, CreatorFeeRow, Totals } from './types';

export { currentUtcMonth, hoursText, monthLabel, num, previousUtcMonth, rewardErrorMessage, vnd } from 'pages/online-reward/utils';

/** 0.015 → "1,5%" (tránh lỗi số thực kiểu 7.000000000000001). */
export const pctText = (rate?: number | null) => `${String(Math.round((rate || 0) * 10000) / 100).replace('.', ',')}%`;

/** Ô nhập % → số thập phân lưu trong chính sách (4 → 0.04). */
export const pctToRate = (v: number | null | undefined) => (v === null || v === undefined ? 0 : Math.round(v * 10000) / 1000000);
export const rateToPct = (rate: number) => Math.round(rate * 1000000) / 10000;

const emptyTotals = (): Totals => ({
  creators: 0, kc: 0, a: 0, b: 0, c: 0, d: 0, fee: 0, feeGross: 0, companyKeep: 0,
  creatorReward: 0, creatorDirect: 0, toAgency: 0, total: 0, ruled: 0,
});

export function addCreator(t: Totals, r: CreatorFeeRow) {
  t.creators += 1;
  t.kc += r.kc;
  t.a += r.a;
  t.b += r.b;
  t.c += r.c;
  t.d += r.dFee;
  t.fee += r.fee;
  t.feeGross += r.feeGross;
  t.companyKeep += r.companyKeep;
  t.creatorReward += r.creatorReward;
  t.creatorDirect += r.creatorDirect;
  t.toAgency += r.toAgency;
  t.total += r.total;
  if (r.rule) t.ruled += 1;
}

/** "7359…558, 7662…489\n…" → danh sách UID (dãy 5–32 chữ số), bỏ trùng, giữ thứ tự. */
export const parseUids = (text: string): string[] => Array.from(new Set(String(text || '').match(/\d{5,32}/g) ?? []));

/** Nhãn ngắn kiểu Creator đặc biệt. */
export const ruleLabel = (r: Pick<CreatorFeeRow, 'rule' | 'agencyPct'>) =>
  r.rule === 'SHARE' ? `Chia phí · Agency ${String(r.agencyPct).replace('.', ',')}%` : r.rule === 'DIRECT_PAY' ? 'Cty trả thẳng Creator' : '';

const norm = (s: string | null | undefined) => (s || '').trim().toLowerCase();

/**
 * Lọc theo email Đại diện như web cũ: giữ Creator có cột E đúng email đó, gom lại theo Agency.
 * Không có bộ lọc → trả nguyên bảng của máy chủ.
 */
export function viewOf(data: AgencyFeeMonth, email: string, showInternal: boolean): { agencies: AgencyRow[]; totals: Totals } {
  const filter = norm(email);
  const meta = new Map(data.agencies.map((a) => [a.key, a]));
  let agencies: AgencyRow[];
  if (!filter) {
    agencies = data.agencies;
  } else {
    const byKey = new Map<string, AgencyRow>();
    for (const r of data.creators) {
      if (norm(r.agentEmail) !== filter) continue;
      let ag = byKey.get(r.agencyKey);
      if (!ag) {
        const m = meta.get(r.agencyKey);
        ag = { key: r.agencyKey, name: r.agency, shortName: m?.shortName ?? r.agency, excluded: !!m?.excluded, branchOf: m?.branchOf ?? null, ...emptyTotals() };
        byKey.set(r.agencyKey, ag);
      }
      addCreator(ag, r);
    }
    agencies = Array.from(byKey.values()).sort((x, y) => y.total - x.total || y.kc - x.kc);
  }
  const totals = emptyTotals();
  for (const a of agencies) {
    if (a.excluded) continue;
    for (const k of Object.keys(totals) as (keyof Totals)[]) totals[k] += a[k];
  }
  return { agencies: showInternal ? agencies : agencies.filter((a) => !a.excluded), totals };
}

/** Danh sách email Đại diện (gợi ý ô lọc): tổng thanh toán lớn lên trước, rồi nhiều Creator. */
export function emailOptions(creators: CreatorFeeRow[]) {
  const by = new Map<string, { n: number; total: number }>();
  for (const r of creators) {
    const e = norm(r.agentEmail);
    if (!e) continue;
    const x = by.get(e) ?? { n: 0, total: 0 };
    x.n += 1;
    x.total += r.total;
    by.set(e, x);
  }
  return Array.from(by.entries())
    .sort((a, b) => b[1].total - a[1].total || b[1].n - a[1].n || a[0].localeCompare(b[0]))
    .map(([value, x]) => ({ value, label: `${value} · ${x.n} Creator` }));
}

const fold = (s: string) => s.normalize('NFC').toLocaleLowerCase('vi').trim();

export function filterCreators(rows: CreatorFeeRow[], q: string, onlyFee: boolean) {
  const needle = fold(q.replace(/^@/, ''));
  return rows.filter((r) => {
    if (onlyFee && r.fee <= 0 && r.creatorReward <= 0) return false;
    if (!needle) return true;
    return [r.uid || '', r.tiktokUsername || '', r.agentEmail || ''].some((h) => fold(h).includes(needle));
  });
}

export function warningTexts(d: AgencyFeeMonth): string[] {
  const out: string[] = [];
  if (d.warnings.noData) out.push('Chưa có dữ liệu TikTok của tháng này.');
  else if (!d.warnings.monthFile) out.push('Chưa có file trọn tháng — đang cộng từ các file ngày, số còn đổi.');
  if (d.warnings.outsideMonthFile) {
    out.push(`${d.warnings.outsideMonthFile} UID chỉ có ở file ngày / file một phần tháng, không có trong file trọn tháng — không tính.`);
  }
  if (d.warnings.creatorRewardUnavailable) out.push('Không đọc được Thưởng Online — cột Thưởng Creator đang là 0.');
  if (d.counts.departed) out.push(`${d.counts.departed} Creator "Đã rời đi" (file TikTok không còn UID) được gom vào một Nhóm riêng như web cũ.`);
  if (d.counts.noGroup) out.push(`${d.counts.noGroup} dòng không có Nhóm — bỏ qua.`);
  if (d.counts.vpnt) out.push(`${d.counts.vpnt} Creator có VPNT đã xác nhận — phí Agency từ các Creator này = 0.`);
  if (d.counts.deducted) out.push(`${d.counts.deducted} Creator đã bị trừ ngày/giờ LIVE (khoản tay Thưởng Online).`);
  if (d.counts.ruled) {
    out.push(`${d.counts.ruled} Creator đặc biệt theo UID: công ty trả thẳng thưởng Creator${d.totals.companyKeep ? `, công ty giữ lại ${d.totals.companyKeep.toLocaleString('vi-VN')} đ phí chia` : ''}.`);
  }
  if (d.warnings.unmatchedRuleUids?.length) {
    out.push(`${d.warnings.unmatchedRuleUids.length} UID Creator đặc biệt không có trong số liệu tháng này: ${d.warnings.unmatchedRuleUids.slice(0, 5).join(', ')}${d.warnings.unmatchedRuleUids.length > 5 ? '…' : ''}.`);
  }
  return out;
}
